import { useState, useCallback, useRef, useEffect } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, resetFFmpegEngine } from "@/lib/ffmpegEngine";
import { formatMinSecTenth as formatMinSec, formatDuration, trimAudioSegment, removeAudioSegment, planRemoval, stepOutputFormat } from "./operations";
import { TimeRangeFields } from "./TimeRangeFields";
import { videoHasAudio } from "./containers";
import { filePeaks } from "./waveform";
import { AudioProcessingProgress, showAudioError, FileSizeNotice, useAudioPipelineStep, NO_AUDIO_TRACK_MESSAGE, UNREADABLE_AUDIO_MESSAGE } from "./uiHelpers";

import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Cuts an audio clip down to a specific start and end point (Keep selection), or deletes a section from the middle and joins the two ends (Remove selection), such as a cough or an ad read. A video file works too: its sound is trimmed and saved as audio. Runs in your browser: the audio never leaves your device, but the first use downloads a processing engine from a public CDN.`,
  how: `Upload your audio file (or a video), listen to the preview to find your spot, then drag either end of the range slider to set the start and end points (shown in minutes:seconds, or typed exactly). Choose Keep selection to cut away everything outside the range, or Remove selection to delete the range and keep the rest. You can also add fades, use the 30-second Ringtone button, and watch the waveform. The output keeps the format of the file you uploaded. Listen to the result, then download it.`,
  tip: `This re-encodes the audio to trim precisely at the exact time you specify, unlike a fast video-style copy trim — which means it takes a little real processing time, but the cut point is exact rather than snapped to the nearest keyframe.`,
};

export default function AudioTrimmer() {
  const { inPipeline, intermediate } = useAudioPipelineStep();
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [duration, setDuration] = useState(0);
  const [range, setRange] = useState<[number, number]>([0, 10]);
  const [mode, setMode] = useState<"keep" | "remove">("keep");
  const [rangeValid, setRangeValid] = useState(true);
  const [noAudio, setNoAudio] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [fadeIn, setFadeIn] = useState(0);
  const [fadeOut, setFadeOut] = useState(0);
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setLoadFailed(false);
    setNoAudio(false);
    setRangeValid(true);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
    setDuration(0);
    setRange([0, 10]);
    setPeaks(null);
    const isVideo = f.type.startsWith("video/") || /\.(mp4|m4v|mov|webm|mkv)$/i.test(f.name);
    // A video without a sound track has nothing to trim: say so now, not after the engine has loaded.
    if (isVideo) videoHasAudio(f).then((has) => { if (has === false) setNoAudio(true); });
    // The waveform decodes the whole file in memory, so it is skipped for big videos.
    if (!isVideo || f.size < 60 * 1024 * 1024) filePeaks(f).then(setPeaks);
  }, []);

  // Draw the waveform; the selected part is highlighted, the rest dimmed.
  useEffect(() => {
    const c = canvasRef.current; if (!c || !peaks || !duration) return;
    const ctx = c.getContext("2d")!; const W = c.width, H = c.height;
    ctx.clearRect(0, 0, W, H);
    const x0 = (range[0] / duration) * W, x1 = (range[1] / duration) * W;
    peaks.forEach((v, i) => {
      const x = (i / peaks.length) * W;
      const inside = x >= x0 && x <= x1;
      // Keep mode highlights what stays; Remove mode shades the part that goes (red) and highlights the rest.
      ctx.fillStyle = mode === "keep" ? (inside ? "#6366f1" : "rgba(128,128,128,0.45)") : (inside ? "rgba(220,60,60,0.7)" : "#6366f1");
      const h = Math.max(1, v * H);
      ctx.fillRect(x, (H - h) / 2, Math.max(1, W / peaks.length - 0.5), h);
    });
  }, [peaks, range, duration, mode]);

  const onLoadedMetadata = () => {
    const d = audioRef.current?.duration;
    if (d && Number.isFinite(d)) {
      setDuration(d);
      const end = d; // whole file by default; a fixed 10 s silently dropped the rest
      setRange([0, end]);
    }
  };

  // Keep the audio player's own playhead in sync when the range is
  // adjusted, so previewing the selected start point is one click away.
  const previewFromStart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = range[0];
      audioRef.current.play();
    }
  };

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
  };

  const trim = async () => {
    if (!file) return;
    const [start, end] = range;
    if (end <= start) {
      toast.error("End time must be after start time");
      return;
    }
    if (mode === "remove") {
      const plan = planRemoval(duration, start, end);
      if (plan.error) { toast.error(plan.error); return; }
    }
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading audio engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading audio file...");
      const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp3";
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(file));

      const fmtOut = stepOutputFormat(file, intermediate);
      setStatusMessage(mode === "remove" ? "Removing the section..." : "Trimming...");
      const data = mode === "remove"
        ? (await removeAudioSegment(ffmpeg, `input.${ext}`, `output.${fmtOut.value}`, duration, start, end, setProgress, controller.signal, { codec: fmtOut.codec, fadeIn, fadeOut })).data
        : await trimAudioSegment(ffmpeg, `input.${ext}`, `output.${fmtOut.value}`, start, end, setProgress, controller.signal, { codec: fmtOut.codec, fadeIn, fadeOut });

      setResult(ffmpegOutputToBlob(data, fmtOut.mime));
      toast.success(mode === "remove" ? "Section removed!" : "Audio trimmed!");
    } catch (err) {
      showAudioError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="audio/*,video/*" maxSize={500} onFiles={handleFiles} label="Drop an audio file to trim" description="Keep a range or cut a section out. A video works too: its sound is trimmed." />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name}</p></div>
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}

          <audio ref={audioRef} src={previewUrl} controls onLoadedMetadata={onLoadedMetadata} onError={() => { setLoadFailed(true); }} className="w-full" />
          {noAudio && (
            <div role="alert" data-testid="no-audio-track" className="p-3 rounded-lg border border-destructive/50 text-sm text-destructive flex items-center justify-between gap-3">
              <span>{NO_AUDIO_TRACK_MESSAGE} There is nothing to trim. Nothing was used from your trial.</span>
              <Button size="sm" variant="outline" onClick={() => { setFile(null); setNoAudio(false); setDuration(0); }}>Choose another</Button>
            </div>
          )}
          {loadFailed && !noAudio && (
            <div role="alert" className="p-3 rounded-lg border border-destructive/50 text-sm text-destructive flex items-center justify-between gap-3">
              <span>{UNREADABLE_AUDIO_MESSAGE}</span>
              <Button size="sm" variant="outline" onClick={() => { setFile(null); setLoadFailed(false); setDuration(0); }}>Choose another</Button>
            </div>
          )}

          {duration > 0 && !noAudio && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3" role="radiogroup" aria-label="What to do with the selection">
                  <Label className="text-xs">Selection</Label>
                  {([["keep", "Keep selection"], ["remove", "Remove selection"]] as const).map(([m, label]) => (
                    <Button key={m} size="sm" variant={mode === m ? "default" : "outline"} className="h-7 text-xs" role="radio" aria-checked={mode === m} onClick={() => { setMode(m); setResult(null); }}>{label}</Button>
                  ))}
                </div>
                <div className="flex gap-1">
                  {mode === "keep" && (
                    <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => {
                      const end = Math.min(duration, range[0] + 30);
                      setRange([range[0], end]); setResult(null);
                    }}>Ringtone (30 s)</Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={previewFromStart}>Preview from start</Button>
                </div>
              </div>
              {peaks && <canvas ref={canvasRef} width={600} height={64} className="w-full h-16 rounded border bg-card" aria-label="Waveform of the file; the selected part is highlighted" data-testid="waveform" />}
              <Slider
                aria-label="Trim range"
                value={range}
                onValueChange={(v) => {
                  const next = [v[0], v[1]] as [number, number];
                  setRange(next);
                  setResult(null);
                }}
                min={0}
                max={duration}
                step={0.1}
                minStepsBetweenThumbs={1}
              />
              <TimeRangeFields range={range} duration={duration} onChange={(r) => { setRange(r); setResult(null); }} onValidChange={setRangeValid} />
              <div className="flex justify-between text-xs text-muted-foreground font-mono">
                <span>{formatMinSec(range[0])}</span>
                <span>Total: {formatMinSec(duration)}</span>
                <span>{formatMinSec(range[1])}</span>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-xs">
                <label className="flex items-center gap-1.5">Fade in <select className="border rounded px-1 py-0.5 bg-background" value={fadeIn} onChange={(e) => { setFadeIn(Number(e.target.value)); setResult(null); }}>{[0, 0.5, 1, 2, 3, 5].map((n) => <option key={n} value={n}>{n ? `${n} s` : "none"}</option>)}</select></label>
                <label className="flex items-center gap-1.5">Fade out <select className="border rounded px-1 py-0.5 bg-background" value={fadeOut} onChange={(e) => { setFadeOut(Number(e.target.value)); setResult(null); }}>{[0, 0.5, 1, 2, 3, 5].map((n) => <option key={n} value={n}>{n ? `${n} s` : "none"}</option>)}</select></label>
              </div>
              <p className="text-xs text-muted-foreground text-center" data-testid="trim-summary">
                {mode === "keep"
                  ? `Keeping ${formatDuration(range[1] - range[0])} of ${formatDuration(duration)}`
                  : (() => { const plan = planRemoval(duration, range[0], range[1]); return plan.error ? plan.error : `Removing ${formatDuration(Math.min(duration, range[1]) - range[0])} of ${formatDuration(duration)}, leaving ${formatDuration(plan.outSeconds)}`; })()}
              </p>
            </div>
          )}

          {processing && <AudioProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          {result && (
            <div className="space-y-1">
              <p className="text-sm font-medium">Listen to the result</p>
              <ResultPreview blob={result} />
            </div>
          )}
          <div className="flex gap-3">
            {result ? (
              <>
                <DownloadButton blob={result} fileName={`${mode === "remove" ? "cut" : "trimmed"}_${file.name.replace(/\.[^.]+$/, `.${stepOutputFormat(file, intermediate).value}`)}`} />
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={trim} disabled={processing || sizeCheck?.severity !== "ok" || duration === 0 || !rangeValid || noAudio}>{processing ? (mode === "remove" ? "Removing..." : "Trimming...") : (mode === "remove" ? "Remove Section" : "Trim Audio")}</Button>
                <Button variant="outline" onClick={() => setFile(null)} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
